import { Button, IconProps } from "@amzn/awsui-components-react";

/**
 * A button for triggering async operations where we submit a request,
 * wait to hear a response, and then show a success or failure.
 * This is useful for submitting redrives.
 *
 * - Initial state: Shows label with specified icon, clickable
 * - Loading state: Shows spinner, disabled
 * - Successful state: Shows label with success icon, disabled
 * - Failed state: Shows label with error icon, disabled with error message on hover
 */

export enum AsyncStatus {
  NOT_STARTED = "NOT_STARTED",
  LOADING = "LOADING",
  SUCCESSFUL = "SUCCESSFUL",
  FAILED = "FAILED",
}

export const asyncStatusMap = {
  unknown: AsyncStatus.NOT_STARTED,
  loading: AsyncStatus.LOADING,
  success: AsyncStatus.SUCCESSFUL,
  error: AsyncStatus.FAILED,
} as const;

interface AsyncActionButtonProps {
  label: string;
  status: AsyncStatus;
  onClick: () => void;
  errorMessage?: string;
  successMessage?: string;
  successLabel?: string;
  failedLabel?: string;
  iconName?: IconProps.Name;
}

export const AsyncActionButton: React.FC<AsyncActionButtonProps> = ({
  label,
  status,
  onClick,
  errorMessage,
  successMessage = "Processed successfully",
  successLabel,
  failedLabel,
  iconName = "refresh",
}) => {
  const isDisabled = status !== AsyncStatus.NOT_STARTED;

  const getIconName = (): IconProps.Name => {
    switch (status) {
      case AsyncStatus.LOADING:
        return "status-in-progress";
      case AsyncStatus.SUCCESSFUL:
        return "status-positive";
      case AsyncStatus.FAILED:
        return "status-negative";
      default:
        return iconName;
    }
  };

  const getDisabledReason = () => {
    if (status === AsyncStatus.FAILED) return errorMessage;
    if (status === AsyncStatus.SUCCESSFUL) return successMessage;
    return undefined;
  };

  const getLabel = () => {
    if (status === AsyncStatus.SUCCESSFUL && successLabel) return successLabel;
    if (status === AsyncStatus.FAILED && failedLabel) return failedLabel;
    return label;
  };

  return (
    <Button
      onClick={onClick}
      disabled={isDisabled}
      iconName={getIconName()}
      loading={status === AsyncStatus.LOADING}
      disabledReason={getDisabledReason()}
    >
      {getLabel()}
    </Button>
  );
};
